/**
 * Repères de mesure PostHog de la liste (docs/tracking-data-attr.md, section « Liste »).
 *
 * Passifs : rien n'est envoyé ici, PostHog autocapture lit `data-attr` et les attributs
 * `data-ph-capture-attribute-<clé>` de l'élément cliqué et de ses parents. Aucun style ni script ne doit
 * lire ces attributs. Jamais de texte saisi dans une valeur (la recherche tapée n'y figure pas).
 */

/** Préfixe PostHog : la clé qui suit devient une propriété du clic autocapturé. */
const CAPTURE_PREFIX = "data-ph-capture-attribute-";

type AttributeTarget = Pick<Element, "getAttribute" | "setAttribute">;

/** Pose un attribut seulement s'il change (évite de réveiller les MutationObserver de la page). */
function setIfChanged(el: AttributeTarget, name: string, value: string): void {
    if (el.getAttribute(name) !== value) el.setAttribute(name, value);
}

/** Propriété PostHog posée sur un élément (`data-ph-capture-attribute-<key>`). */
export function setCaptureAttribute(el: AttributeTarget, key: string, value: string | number): void {
    setIfChanged(el, `${CAPTURE_PREFIX}${key}`, String(value));
}

/** « Voir les N véhicules » : N part avec le clic (`results`), pour compter les « 0 véhicule ». */
export function setResultsCount(el: AttributeTarget, nbHits: number): void {
    setCaptureAttribute(el, "results", Math.max(0, Math.trunc(nbHits)));
}

/**
 * Contexte de la liste, posé par le layout sur `<body>` (MarketplaceChromeHelper::listingTrackingContext) :
 * tenu à jour sans rechargement. `filtered` suit la règle de « liste filtrée » (`isListingFiltered`),
 * `page` est le numéro affiché (1 pour la première page). Sans contexte serveur (page hors liste), rien.
 */
export function syncListingTrackingContext(
    filtered: boolean,
    page: number,
    body: (AttributeTarget & Pick<Element, "hasAttribute">) | null = typeof document === "undefined" ? null : document.body,
): void {
    if (!body || !body.hasAttribute(`${CAPTURE_PREFIX}listing_type`)) return;
    setCaptureAttribute(body, "listing_filtered", filtered ? "yes" : "no");
    setCaptureAttribute(body, "listing_page", Math.max(1, Math.trunc(page) || 1));
}

/**
 * Champ de recherche de la liste (autocomplétion) : ses contrôles sont rendus par la bibliothèque, sans
 * gabarit ; on les repère après le montage. Les suggestions sont repérées sur le panneau (`render`).
 */
export const SEARCH_CONTROL_MARKERS: ReadonlyArray<readonly [string, string]> = [
    [".aa-Input", "listing-search-input"],
    [".aa-Label", "listing-search-submit"],
    [".aa-SubmitButton", "listing-search-submit"],
    [".aa-SearchButton", "listing-search-submit"],
    [".aa-ClearButton", "listing-search-clear"],
];

export function markListingSearchControls(container: Pick<Element, "querySelectorAll"> | null): void {
    markSearchControls(container, "listing-search");
}

/**
 * Même repérage pour une autre recherche (ex. « header-search » dans l'en-tête des pages hors liste) :
 * le préfixe remplace « listing-search » dans chaque repère.
 */
export function markSearchControls(container: Pick<Element, "querySelectorAll"> | null, prefix: string): void {
    if (!container) return;
    for (const [selector, marker] of SEARCH_CONTROL_MARKERS) {
        const value = marker.replace(/^listing-search/, prefix);
        container.querySelectorAll(selector).forEach((el) => setIfChanged(el, "data-attr", value));
    }
}

/** Panneau des suggestions : chaque suggestion, récente ou marque, cliquée dedans porte ce repère. */
export function markListingSearchPanel(root: AttributeTarget | null | undefined): void {
    markSearchPanel(root, "listing-search-suggestion");
}

export function markSearchPanel(root: AttributeTarget | null | undefined, marker: string): void {
    if (root) setIfChanged(root, "data-attr", marker);
}
